import { useState } from "react";
import { submitToHubspot } from "../utils/hubspot";
import { FaCheckCircle, FaPaperPlane } from "react-icons/fa";
import styles from "../pages/Home/Contact.module.scss";
import { PRACTICES } from "../content/services";

const CONTACT_GUID = "44142404-bb21-40c8-98df-2009d5afde95";

const initialFormState = {
    firstName: "",
    lastName: "",
    email: "",
    company: "",
    phone: "",
    industry: "",
    service: "",
    budget: "",
    message: "",
};

  const HUBSPOT_FIELD_MAP: Record<keyof typeof initialFormState, string | null> = {
    firstName: "firstname",
    lastName: "lastname",
    email: "email",
    company: "company",
    phone: "phone",
    industry: "industry",
    service: "service_interest",
    budget: null,
    message: "message",
  };

export default function ContactForm() {
    const [form, setForm] = useState(initialFormState);
  const [status, setStatus] = useState("idle"); // idle | loading | error
    const [errorMessage, setErrorMessage] = useState("");
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setStatus("loading");
        setErrorMessage("");

      const enrichedMessage = [
        form.message.trim(),
        form.budget.trim() ? `Project Budget: ${form.budget.trim()}` : "",
      ]
        .filter(Boolean)
        .join("\n");

      const fields = (Object.entries(form) as [keyof typeof initialFormState, string][])
        .filter(([name, value]) => value.trim() !== "" && HUBSPOT_FIELD_MAP[name])
        .map(([name, value]) => ({
          name: HUBSPOT_FIELD_MAP[name] as string,
          value: name === "message" ? enrichedMessage : value.trim(),
        }));

        try {
            await submitToHubspot(CONTACT_GUID, fields);
            setStatus("idle");
            setForm(initialFormState);
            setShowSuccessPopup(true);
        } catch (error) {
            setStatus("error");
            setErrorMessage(error instanceof Error ? error.message : "An unknown error occurred.");
        }
    };

    return (
        <>
        {showSuccessPopup && (
          <div className={styles.successPopup}>
            <div className={styles.popupContent}>
              <FaCheckCircle className={styles.successIcon} />
              <h3>Message Sent!</h3>
              <p>Thank you for contacting us. We will get back to you shortly.</p>
              <button
                type="button"
                onClick={() => setShowSuccessPopup(false)}
                className={styles.closeButton}
              >
                Close
              </button>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
                  <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                      <label htmlFor="firstName">First Name *</label>
                      <input type="text" id="firstName" name="firstName" placeholder="Enter your first name" required onChange={handleChange} />
                    </div>
                    <div className={styles.formGroup}>
                      <label htmlFor="lastName">Last Name *</label>
                      <input type="text" id="lastName" name="lastName" placeholder="Enter your last name" required onChange={handleChange} />
                    </div>
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="email">Email Address *</label>
                    <input type="email" id="email" name="email" placeholder="Enter your email address" required onChange={handleChange} />
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="company">Company</label>
                    <input type="text" id="company" name="company" placeholder="Enter your company name" onChange={handleChange} />
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="phone">Phone Number</label>
                    <input type="tel" id="phone" name="phone" placeholder="Enter your phone number" onChange={handleChange} />
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="industries">Industries *</label>
                    <select id="industries" name="industries" required onChange={handleChange}>
                      <option value="">Select an industries</option>
                      <option value="business-and-consumer-services">Business and Consumer Services</option>
                      <option value="education">Education</option>
                      <option value="energy-and-utilities">Energy and Utilities</option>
                      <option value="financial-services">Financial Services</option>
                      <option value="government">Government</option>
                      <option value="healthcare-and-life-sciences">Healthcare and Life Sciences</option>
                      <option value="manufacturing">Manufacturing</option>
                      <option value="retail-and-hospitatlity">Retail and Hospitality</option>
                      <option value="technology">Technology</option>
                      <option value="telco-and-media">Telco and Media</option>
                      <option value="transportation-and-logistics">Transportation and Logistics</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="service">Service Interest *</label>
                    <select id="service" name="service" required onChange={handleChange}>
                      <option value="">Select a service</option>
                      {PRACTICES.map((practice) => (
                        <optgroup label={practice.name} key={practice.slug}>
                          {practice.services.map((service) => (
                            <option value={service.slug} key={service.slug}>{service.navLabel}</option>
                          ))}
                        </optgroup>
                      ))}
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="budget">Project Budget</label>
                    <select id="budget" name="budget" onChange={handleChange}>
                      <option value="">Select budget range</option>
                      <option value="under-50k">Under $50,000</option>
                      <option value="50k-100k">$50,000 - $100,000</option>
                      <option value="100k-250k">$100,000 - $250,000</option>
                      <option value="250k-500k">$250,000 - $500,000</option>
                      <option value="over-500k">Over $500,000</option>
                    </select>
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="message">Message *</label>
                    <textarea id="message" name="message" placeholder="Tell us about your project and requirements..." required onChange={handleChange}></textarea>
                  </div>

                  {status === "error" && (
                    <p style={{ color: "#b00020", marginBottom: "12px" }}>
                      {errorMessage || "Failed to submit form"}
                    </p>
                  )}

                  <button type="submit" className={styles.submitButton} disabled ={status === "loading"}>
                    <FaPaperPlane /> Send Message
                  </button>
                </form>
        </>
    );
}

